import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const ExpirationCheckerViewResult = ({ result }) => {
  const getRiskLevelColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'high':
        return 'bg-error/20 text-error';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-success/20 text-success';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const formatDate = (isoDate) => {
    try {
      return new Date(isoDate).toLocaleString();
    } catch (e) {
      return isoDate;
    }
  };

  const metadata = result?.metadata || {};
  const expiration = result?.expiration_analysis || {};
  const validation = expiration?.validation || {};
  const status = expiration?.expiration_status || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Validation Results Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ClockClockwise size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Expiration Analysis</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Details */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Validation Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${validation?.is_valid ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
                      {validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {validation?.semantic_type && (
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Type:</span>
                        <span className="capitalize">{validation.semantic_type.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                    
                    {validation?.format_detected && (
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Format:</span>
                        <span className="capitalize">{validation.format_detected.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                    
                    {validation?.interpreted_date && (
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Interpreted Date:</span>
                        <span>{formatDate(validation.interpreted_date)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expiration Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Expiration Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${status?.is_expired ? 'bg-error/20 text-error' : 'bg-success/20 text-success'}`}>
                      {status?.is_expired ? 'Expired' : 'Valid'}
                    </span>
                  </div>

                  {status?.expiry_risk_level && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-base-content/70">Risk Level:</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getRiskLevelColor(status.expiry_risk_level)}`}>
                        {status.expiry_risk_level}
                      </span>
                    </div>
                  )}
                </div>

                {/* Preservation Recommendation */}
                {status?.preservation_recommendation && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Preservation Recommendation</span>
                    <p className="text-sm">{status.preservation_recommendation}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Analysis Details Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Analysis Details</h3>
              </div>

              <div className="space-y-3">
                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence Score</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round((metadata?.confidence_score || 0) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${(metadata?.confidence_score || 0) * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Language Details */}
                {metadata?.language_handling && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Language Information</span>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Detected Language:</span>
                        <span className="uppercase">{metadata.language_handling.detected_language}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Translation Applied:</span>
                        <span>{metadata.language_handling.translation_applied ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Calculation Details */}
                {status?.calculation_anchor && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Calculation Details</span>
                    <div className="space-y-2 text-sm">
                      {status.calculation_anchor.reference_date && (
                        <div className="flex justify-between items-center">
                          <span className="text-base-content/70">Reference Date:</span>
                          <span>{formatDate(status.calculation_anchor.reference_date)}</span>
                        </div>
                      )}
                      {status.calculation_anchor.timezone && (
                        <div className="flex justify-between items-center">
                          <span className="text-base-content/70">Timezone:</span>
                          <span>{status.calculation_anchor.timezone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Timestamp */}
                {metadata?.validation_timestamp && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-base-content/70">Analysis Timestamp:</span>
                      <span>{formatDate(metadata.validation_timestamp)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpirationCheckerViewResult
